import { Component, computed, DestroyRef, forwardRef, inject, signal } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NbToastrService } from '@nebular/theme';
import { AbstractFormControl } from '@shared/form-controls/abstract-form-control';
import { EmailValidationService } from '@services/email-validation.service';
import { BuildingContactDto } from '@artemis-software/wr-api';

@Component({
  selector: 'wr-building-contacts',
  templateUrl: './building-contacts.component.html',
  styleUrls: ['./building-contacts.component.scss'],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BuildingContactsComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => BuildingContactsComponent),
      multi: true,
    },
  ],
})
export class BuildingContactsComponent extends AbstractFormControl<BuildingContactDto[]> implements Validator {

  private readonly toastrService = inject(NbToastrService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly emailValidationService = inject(EmailValidationService);

  private readonly emailValidator = (control: AbstractControl): ValidationErrors | null => {
    const value = (control.value ?? '').toString().trim();
    if (!value) {
      return null;
    }
    return this.emailValidationService.isValidEmail(value) ? null : { email: true };
  };

  readonly formArray = signal<FormArray | undefined>(undefined);

  readonly controls = computed(() => {
    this.disabledSignal();
    return this.formArray()?.controls ?? [];
  });

  override writeValue(value: BuildingContactDto[] | undefined) {
    super.writeValue(value);
    this.formArray.set(this.initForm(value));
  }

  private initForm(value: BuildingContactDto[] | undefined) {
    const formArray = new FormArray((value ?? []).map((contact) => this.buildContactGroup(contact)));

    formArray.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((newValue: any[]) => {
      this.value = newValue;
    });

    return formArray;
  }

  private buildContactGroup(contact?: BuildingContactDto) {
    return new FormGroup({
      id: new FormControl(contact?.id ?? null),
      name: new FormControl(contact?.name ?? '', [Validators.required]),
      email: new FormControl(contact?.email ?? '', [this.emailValidator]),
      phone: new FormControl(contact?.phone ?? ''),
    });
  }

  addContact() {
    this.formArray()?.push(this.buildContactGroup());
  }

  removeContact(index: number) {
    this.formArray()?.removeAt(index);
  }

  override setDisabledState(isDisabled: boolean) {
    super.setDisabledState(isDisabled);
    if (isDisabled) {
      this.formArray()?.disable({ emitEvent: false });
    } else {
      this.formArray()?.enable({ emitEvent: false });
    }
  }

  hasError(index: number, controlName: string): boolean {
    const control = this.formArray()?.at(index)?.get(controlName);
    return !!(control?.invalid && (control.dirty || control.touched));
  }

  override markAsTouched() {
    super.markAsTouched();
    this.formArray()?.markAllAsTouched();
  }

  validate(_control: AbstractControl): ValidationErrors | null {
    return this.formArray()?.invalid ? { contactsInvalid: true } : null;
  }

  async copyToClipboard(value: string, label: string) {
    try {
      await navigator.clipboard.writeText(value);
      this.toastrService.success(value, `${label} kopiert`);
    } catch {
      this.toastrService.danger(`${label} konnte nicht kopiert werden`, 'Kopieren fehlgeschlagen');
    }
  }
}
